import { useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import axios from "axios";
import { toast } from "react-hot-toast";
import { baseApiURL } from "../../baseUrl";
import { sortEnrollmentNo } from "../../utils/enrollmentSorter";

const Reports = () => {
  const [branches, setBranches] = useState([]);
  const [filters, setFilters] = useState({ batch: "", branch: "" });
  const [students, setStudents] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [tab, setTab] = useState("students");
  const [loading, setLoading] = useState(false);

  // Custom sorting function for enrollment numbers
  const sortEnrollmentNumbers = (studentsList, order = 'ascending') => {
    const sorted = [...studentsList].sort(sortEnrollmentNo);
    return order === 'ascending' ? sorted : sorted.reverse();
  };

  useEffect(() => {
    // load branches for dropdown
    axios
      .get(`${baseApiURL()}/branch/getBranch`)
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data?.branches)) {
          setBranches(res.data.branches);
        }
      })
      .catch(() => {});
  }, []);

  const fetchReport = async () => {
    if (!filters.batch && !filters.branch) {
      toast.error("Select at least batch or branch");
      return;
    }
    
    // Validate batch input is a number if provided
    if (filters.batch && isNaN(filters.batch)) {
      toast.error("Batch must be a valid year");
      return;
    }
    
    try {
      setLoading(true);
      const params = {};
      
      // Handle batch parameter correctly
      if (filters.batch) {
        // If batch is provided, send it as is (could be string or number)
        params.batch = filters.batch.toString();
      }
      
      if (filters.branch && filters.branch !== "") {
        params.branch = filters.branch;
      }
      
      const url = tab === "students"
        ? `${baseApiURL()}/student/details/reports/byBatchBranch`
        : `${baseApiURL()}/faculty/details/reports/byBatchBranch`;
      
      console.log("Fetching with params:", params); // For debugging
      
      const res = await axios.get(url, { params });
      
      if (res.data?.success) {
        if (tab === "students") {
          // Sort students by enrollment number
          const sortedStudents = sortEnrollmentNumbers(res.data.students || [], 'ascending');
          setStudents(sortedStudents);
        } else {
          setFaculties(res.data.faculties || []);
        }
        
        if (res.data.students?.length === 0 || res.data.faculties?.length === 0) {
          toast.success("No records found for the selected criteria");
        } else {
          toast.success(`Found ${tab === 'students' ? res.data.students?.length : res.data.faculties?.length} records`);
        }
      }
      else {
        toast.error(res.data?.message || "Failed to load report");
      }
    } catch (e) {
      console.error("Error fetching report:", e);
      toast.error(e.response?.data?.message || "Failed to load report. Please check your filters.");
    } finally {
      setLoading(false);
    }
  };

  const total = tab === "students" ? students.length : faculties.length;

  const batchesPreset = useMemo(() => {
    const current = new Date().getFullYear();
    const list = [];
    for (let y = current; y >= current - 6; y--) list.push(y);
    return list;
  }, []);

  // Function to export to Excel with sorted data
  const exportToExcel = () => {
    const hasData = tab === 'students' ? students.length : faculties.length;
    if (!hasData) {
      toast.error("No data to export");
      return;
    }
    
    // Sort students again before export to ensure correct order
    const dataToExport = tab === 'students' 
      ? sortEnrollmentNumbers(students, 'ascending')
      : faculties;
    
    const exportRows = dataToExport.map((item, idx) => {
      if (tab === 'students') {
        return {
          SNo: idx + 1,
          EnrollmentNo: item.enrollmentNo,
          FirstName: item.firstName || "",
          MiddleName: item.middleName || "",
          LastName: item.lastName || "",
          Branch: item.branch || "",
          Batch: item.batch || "",
          Semester: item.semester || "",
          Section: item.section || "",
          Email: item.email || "",
          Phone: item.phoneNumber || "",
        };
      }
      return {
        SNo: idx + 1,
        EmployeeId: item.employeeId,
        FirstName: item.firstName || "",
        MiddleName: item.middleName || "",
        LastName: item.lastName || "",
        Department: item.department || "",
        Batch: item.batch || "",
        Email: item.email || "",
        Phone: item.phoneNumber || "",
        Post: item.post || "",
      };
    });
    
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, tab === 'students' ? "Student Report" : "Faculty Report");
    
    const nameParts = [];
    if (filters.batch) nameParts.push(`Batch-${filters.batch}`);
    if (filters.branch) nameParts.push(`Branch-${filters.branch}`);
    const fileName = `${tab === 'students' ? 'Student' : 'Faculty'}_Report${nameParts.length ? `_${nameParts.join("_")}` : ""}.xlsx`;
    
    XLSX.writeFile(wb, fileName);
    toast.success("Excel file downloaded successfully!");
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bento-header-banner flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Reports</h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium text-xs mt-0.5">Filter and export student and faculty records by batch and branch</p>
        </div>
      </div>

      {/* Sub-tab Navigation */}
      <div className="bento-card p-1.5 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl">
        <div className="grid grid-cols-2 gap-2">
          <button
            className={`px-4 py-2 rounded-xl font-bold text-xs transition cursor-pointer ${
              tab === 'students'
                ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200/60 dark:border-slate-700"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
            onClick={() => setTab('students')}
          >
            Students
          </button>
          <button
            className={`px-4 py-2 rounded-xl font-bold text-xs transition cursor-pointer ${
              tab === 'faculty'
                ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200/60 dark:border-slate-700"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
            }`}
            onClick={() => setTab('faculty')}
          >
            Faculty
          </button>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Batch Year</label>
            <input
              type="text"
              value={filters.batch}
              onChange={(e) => {
                if (e.target.value === '' || /^\d+$/.test(e.target.value)) {
                  setFilters({ ...filters, batch: e.target.value });
                }
              }}
              placeholder="e.g., 2022"
              className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
            />
            <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-1">
              Suggested: {batchesPreset.join(", ")}
            </div>
          </div>
          
          <div>
            <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Branch</label>
            <select
              value={filters.branch}
              onChange={(e) => setFilters({ ...filters, branch: e.target.value })}
              className="w-full px-3.5 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-900 dark:text-white text-xs font-medium focus:ring-2 focus:ring-indigo-500 outline-none"
            >
              <option value="">All Branches</option>
              {branches.map((b) => (
                <option key={b._id || b.name} value={b.name}>{b.name}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-end">
            <button 
              onClick={fetchReport} 
              disabled={loading}
              className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2.5 rounded-xl w-full text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed transition shadow-xs cursor-pointer"
            >
              {loading ? "Loading..." : "Generate Report"}
            </button>
          </div>
          
          <div className="flex items-end">
            <div className="w-full bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 px-3.5 py-2 rounded-xl border border-emerald-200 dark:border-emerald-800 flex items-center justify-between">
              <span className="text-xs font-semibold">Total Records:</span>
              <span className="text-base font-bold">{total}</span>
            </div>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={exportToExcel}
              disabled={total === 0}
              className="bg-indigo-600 hover:bg-indigo-500 text-white px-4 py-2.5 rounded-xl w-full text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed transition shadow-xs cursor-pointer"
            >
              Export to Excel
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="overflow-x-auto">
        {tab === 'students' && (
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 overflow-hidden">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
              <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">S.No</th>
                  <th className="px-4 py-3 text-left">Enrollment No</th>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Branch</th>
                  <th className="px-4 py-3 text-left">Batch</th>
                  <th className="px-4 py-3 text-left">Semester</th>
                  <th className="px-4 py-3 text-left">Section</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Phone</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                {students.map((s, index) => (
                  <tr key={s._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-slate-400">{index + 1}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-mono font-bold text-indigo-600 dark:text-indigo-400">{s.enrollmentNo}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-semibold text-slate-900 dark:text-white">
                      {[s.firstName, s.middleName, s.lastName].filter(Boolean).join(" ")}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300 font-medium">{s.branch}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500 dark:text-slate-400">{s.batch || "-"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="px-2 py-0.5 inline-flex text-xs font-bold rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-100 dark:border-indigo-800">
                        Semester {s.semester}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500 dark:text-slate-400">{s.section || "-"}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500 dark:text-slate-400">{s.email}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500 dark:text-slate-400">{s.phoneNumber}</td>
                  </tr>
                ))}
                {!students.length && (
                  <tr>
                    <td colSpan={9} className="px-6 py-12 text-center text-slate-400 dark:text-slate-500">
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 text-slate-300 dark:text-slate-700 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-base font-bold text-slate-800 dark:text-slate-200">No student data found</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Try adjusting your filters or generate a new report</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'faculty' && (
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 overflow-hidden">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
              <thead className="bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 font-bold uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3 text-left">S.No</th>
                  <th className="px-4 py-3 text-left">Employee ID</th>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Department</th>
                  <th className="px-4 py-3 text-left">Batch</th>
                  <th className="px-4 py-3 text-left">Post</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Phone</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-slate-800 dark:text-slate-200">
                {faculties.map((f, index) => (
                  <tr key={f._id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-slate-400">{index + 1}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-mono font-bold text-indigo-600 dark:text-indigo-400">{f.employeeId}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-semibold text-slate-900 dark:text-white">
                      {[f.firstName, f.middleName, f.lastName].filter(Boolean).join(" ")}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-600 dark:text-slate-300 font-medium">{f.department}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500 dark:text-slate-400">{f.batch || "-"}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="px-2 py-0.5 inline-flex text-xs font-bold rounded-lg bg-purple-50 dark:bg-purple-950/60 text-purple-700 dark:text-purple-300 border border-purple-100 dark:border-purple-800">
                        {f.post || "Faculty"}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500 dark:text-slate-400">{f.email}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500 dark:text-slate-400">{f.phoneNumber}</td>
                  </tr>
                ))}
                {!faculties.length && (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-slate-400 dark:text-slate-500">
                      <div className="flex flex-col items-center">
                        <svg className="w-12 h-12 text-slate-300 dark:text-slate-700 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                        <p className="text-base font-bold text-slate-800 dark:text-slate-200">No faculty data found</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Try adjusting your filters or generate a new report</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports;